import { RefObject, useCallback, useEffect, useRef } from 'react';

interface UseTableKeyboardNavigationProps<TData> {
  data: TData[];
  onRowClick?: (row: TData) => void;
  containerRef: RefObject<HTMLDivElement | null>;
  enabled?: boolean;
}

/**
 * Hook to handle keyboard navigation (ArrowUp/ArrowDown) for a data table.
 *
 * Conceptually separate from bulk selection:
 * - Maintains an internal "active" row index (the last row you navigated to)
 * - Does NOT rely on checkbox selection state
 * - Simulates row click behavior on navigation
 * - Automatically scrolls the active row into view
 */
export const useTableKeyboardNavigation = <TData>({
  data,
  onRowClick,
  containerRef,
  enabled = true,
}: UseTableKeyboardNavigationProps<TData>) => {
  // Track the index of the last "active" row (via click or keyboard)
  const activeRowIndexRef = useRef<number | null>(null);

  // Reset active index when data changes (filtering, sorting, pagination)
  // This prevents "phantom" navigation where the index points to a stale or out-of-bounds row
  useEffect(() => {
    activeRowIndexRef.current = null;
  }, [data]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!enabled || !onRowClick || data.length === 0) return;

      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;

      e.preventDefault();

      const maxIndex = data.length - 1;
      const currentIndex = activeRowIndexRef.current ?? -1;
      let newIndex = currentIndex;

      if (e.key === 'ArrowDown') {
        // If nothing active yet, start at the top; otherwise move down, clamped to maxIndex
        newIndex = currentIndex < 0 ? 0 : Math.min(currentIndex + 1, maxIndex);
      } else if (e.key === 'ArrowUp') {
        // If nothing active yet, start at the bottom; otherwise move up, clamped to 0
        newIndex = currentIndex < 0 ? maxIndex : Math.max(currentIndex - 1, 0);
      }

      if (newIndex === currentIndex || newIndex < 0) return;

      activeRowIndexRef.current = newIndex;
      const newRow = data[newIndex];

      // Trigger the same behavior as a row click
      onRowClick(newRow);

      // Scroll the corresponding <tr> into view within the scroll container
      if (containerRef.current) {
        const rowElement = containerRef.current.querySelector<HTMLElement>(
          `tbody tr:nth-child(${newIndex + 1})`,
        );
        rowElement?.scrollIntoView({ block: 'nearest' });
      }
    },
    [containerRef, data, enabled, onRowClick],
  );

  const setActiveIndexFromClick = useCallback(
    (rowIndex: number) => {
      // Row index from click is always trusted source of truth
      if (rowIndex >= 0 && rowIndex < data.length) {
        activeRowIndexRef.current = rowIndex;
      } else {
        activeRowIndexRef.current = null;
      }
    },
    [data.length],
  );

  return { handleKeyDown, setActiveIndexFromClick };
};
